import React, { Fragment, useCallback, useRef } from 'react';
import type { FC, RefObject } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { AnnotationProviders } from '@atlaskit/editor-common/types/annotation';
import type RendererActions from '@atlaskit/renderer/actions';
import { AnnotationsWrapper } from '@atlaskit/renderer/annotations';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import { WithRendererActions } from '@atlaskit/renderer/actions/with-renderer-actions';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useSubscriptionsDispatchContext } from '@confluence/graphql/entry-points/SubscriptionsContextProvider.ts';
import { RendererAnnotationProviderComponents } from '@confluence/annotation-provider-inline-comments/entry-points/RendererAnnotationProviderComponents.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { InlineCommentQueryParamsListener } from '@confluence/comments-query-param-handlers/entry-points/InlineCommentQueryParamsListener.ts';
import { useAnnotations } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import {
	useDocumentUpdateStatus,
	useDocumentUpdateStatusDispatch,
} from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import { RendererActionsListener } from '@confluence/renderer-actions/entry-points/RendererActionsListener.ts';
import { usePageState } from '@confluence/page-context';
import {
	MissedPubSubCommentEventsHandler,
	setUpRendererInlineCommentsPubSub,
	removeRendererInlineCommentsPubSub,
	CommentEventType,
	type MissedCommentsResult,
} from '@confluence/comments-pubsub';
import { ContentRefetchHandler } from '@confluence/content-body/entry-points/ContentRefetchHandler.ts';
import {
	useContentRefetchDispatch,
	useContentRefetch,
} from '@confluence/content-body/entry-points/ContentRefetchContext.ts';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments.ts';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { FABRIC_COMMENTS_HIGHLIGHTS_METRIC } from '@confluence/annotation-provider-inline-comments/entry-points/pageSegmentLoadMetrics.ts';
import {
	useCommentsDataActions,
	useCommentsDataState,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useUpdateAnnotations } from '@confluence/comments-data/entry-points/useUpdateAnnotations.ts';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useIsHighlightOptionPanelEnabled } from '@confluence/comments-util/entry-points/useIsHighlightOptionPanelEnabled.ts';

const SelectionComponentLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-annotation-provider-SelectionComponent" */ '@confluence/annotation-provider-inline-comments/entry-points/SelectionComponent.ts'
			)
		).SelectionComponent,
});

const HoverComponentLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-annotation-provider-HoverComponent" */ '@confluence/annotation-provider-inline-comments/entry-points/HoverComponent.ts'
			)
		).HoverComponent,
});

const ViewComponentLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-annotation-provider-ViewComponent" */ '@confluence/annotation-provider-inline-comments/entry-points/ViewComponent.ts'
			)
		).ViewComponent,
});

const InlineCommentLinkHandlerLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-InlineCommentLinkHandler" */ '@confluence/inline-comments-link-handler'
			)
		).InlineCommentLinkHandler,
});

type InlineCommentsPubSubHandler = {
	contentId: string;
	contentType?: 'page' | 'blogpost' | 'whiteboard' | 'database' | 'embed' | 'slide' | 'folder';
	updateDocument: (newADF: JSONDocNode) => void;
	rendererActions: RendererActions;
	isNestedRender?: boolean;
};

const InlineCommentsPubSubHandler: FC<InlineCommentsPubSubHandler> = ({
	contentId,
	contentType,
	updateDocument,
	rendererActions,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setDocumentUpdated } = useDocumentUpdateStatusDispatch();
	const { setRefetchContent } = useContentRefetchDispatch();
	const { documentNeedsUpdate, publishedDocumentVersion } = useDocumentUpdateStatus();
	const { addSubscriber, removeSubscriber, setContentType, setContentId } =
		useSubscriptionsDispatchContext() || {};
	const subscriptions = useRef<{ delta: string; subscriberId: string }[]>([]);
	const { setDocumentNeedsUpdate, setPublishedDocumentVersion } = useDocumentUpdateStatusDispatch();
	const { userId } = useSessionData();
	const { updateUnresolvedInlineComment } = useInlineCommentsActions();
	const { setOrderedActiveAnnotationIdList, updateRemovedCommentIdsMap, reopenParentComment } =
		useCommentsDataActions();
	const { orderedActiveAnnotationIdList } = useCommentsDataState();
	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();
	const { setActiveHighlight } = useInlineCommentsDispatchContext() || {};
	const { deleteCommentInCache, addCommentInCache } = useAllCommentsData({
		contentId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});

	const updateUnresolvedInlineCommentFn = useUpdateAnnotations([
		updateUnresolvedInlineComment,
		setOrderedActiveAnnotationIdList,
	]);

	const unsubscribeFromPubSub = useCallback(() => {
		if (subscriptions.current.length === 0) {
			return;
		}

		removeRendererInlineCommentsPubSub({
			contentId,
			contentType,
			removeSubscriber,
			subscriptions: subscriptions.current,
		});

		subscriptions.current = [];
	}, [contentId, contentType, removeSubscriber]);

	const subscribeToPubSub = useCallback(() => {
		if (subscriptions.current.length > 0) {
			// Multiple active subscriptions will cause multiple cache-writes, which we don't want.
			// Instead of returning early here, we unsub + resub so that values in setUpRendererInlineCommentsPubSub stay up to date.
			unsubscribeFromPubSub();
		}

		subscriptions.current =
			setUpRendererInlineCommentsPubSub({
				contentId,
				currentUserId: userId,
				contentType,
				rendererActions,
				updateDocument,
				updateUnresolvedInlineComment: updateUnresolvedInlineCommentFn,
				setContentNeedsUpdate: setDocumentNeedsUpdate,
				needsContentUpdate: Boolean(documentNeedsUpdate),
				setPublishedDocumentVersion,
				publishedDocumentVersion,
				createAnalyticsEvent,
				updateRemovedCommentIdsMap,
				reopenParentComment,
				addSubscriber,
				setContentType,
				setContentId,
				updateUnreadCommentIDSet,
				addCommentInCache,
				deleteCommentInCache,
				setActiveHighlight,
				setOrderedActiveAnnotationIdList,
				orderedActiveAnnotationIdList,
			}) || [];

		return subscriptions.current;
		// We only need to respond to values that CAN change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		contentId,
		userId,
		contentType,
		rendererActions,
		documentNeedsUpdate,
		publishedDocumentVersion,
		addSubscriber,
		setContentType,
		setContentId,
		addCommentInCache,
		deleteCommentInCache,
		unsubscribeFromPubSub,
	]);

	const handleFetchContentVersionEvent = useCallback(() => {
		setDocumentNeedsUpdate(true);
	}, [setDocumentNeedsUpdate]);

	const handleEventFetchSuccess = useCallback(
		(res: MissedCommentsResult) => {
			// TODO: Parse the events returned for 'resolved', 'unresolved', and 'delete' when they are added
			//       for now we just reload the content when the user comes back
			// For now all we have is create events, which means we will miss things, but only update if a new ones were created
			if (res.commentEvents.length > 0) {
				setDocumentUpdated(true);
				setRefetchContent(true);
			}
		},
		[setRefetchContent, setDocumentUpdated],
	);

	if (!userId) {
		return null;
	} else {
		return (
			<MissedPubSubCommentEventsHandler
				contentId={contentId!}
				eventType={CommentEventType.INLINE}
				subscribeToPubSubEvents={subscribeToPubSub}
				unsubscribeFromPubSubEvents={unsubscribeFromPubSub}
				onEventFetchSuccess={handleEventFetchSuccess}
				onFetchContentVersionEvent={handleFetchContentVersionEvent}
				inactiveInterval={0}
				returnInterval={0}
			/>
		);
	}
};

type WithAnnotationsWrapperProps = {
	annotationProvider: AnnotationProviders | null | undefined;
	rendererRef: RefObject<HTMLDivElement>;
	adfDocument: DocNode;
	children: JSX.Element;
	isNestedRender?: boolean;
	updateDocument: (newADF: JSONDocNode) => void;
	lastModifiedDate?: string | null;
	isArchived?: boolean;
	preloadedCanAddComments?: boolean;
};

type BaseProps = WithAnnotationsWrapperProps & WithFlagsProps;

const WithAnnotationsWrapperComponent: FC<BaseProps> = ({
	flags,
	annotationProvider,
	rendererRef,
	adfDocument,
	updateDocument,
	isNestedRender = false,
	children,
	lastModifiedDate,
	isArchived = false,
	preloadedCanAddComments,
}: BaseProps) => {
	const { setDocumentUpdated, setDocumentNeedsUpdate, setPublishedDocumentVersion } =
		useDocumentUpdateStatusDispatch();
	const { refetchContent } = useContentRefetch();
	const { setRefetchContent } = useContentRefetchDispatch();
	const [{ contentId, contentType }] = usePageState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { selectionProps, viewProps, hoverProps, isOffline } = useAnnotations();
	const { isHighlightOptionPanelEnabled } = useIsHighlightOptionPanelEnabled();

	const handleAnnotationsLoaded = useCallback(
		({ numberOfUnresolvedInlineComments }: { numberOfUnresolvedInlineComments: number }) => {
			const options = {
				customData: { isFabricPage: true, numberOfUnresolvedInlineComments },
			};
			FABRIC_COMMENTS_HIGHLIGHTS_METRIC.stop(options);
		},
		[],
	);

	const handleOnRefetchComplete = useCallback(
		(versionNumber?: number | null) => {
			setRefetchContent(false);
			setDocumentUpdated(true);
			setDocumentNeedsUpdate(false);
			void flags.hideAllFlags();

			if (versionNumber) {
				setPublishedDocumentVersion(versionNumber);
			}

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'refetched',
					actionSubject: 'content',
					objectId: contentId,
					contentType,
					source: 'viewPageScreen',
				},
			}).fire();
		},
		[
			setRefetchContent,
			setDocumentUpdated,
			createAnalyticsEvent,
			contentId,
			contentType,
			setDocumentNeedsUpdate,
			flags,
			setPublishedDocumentVersion,
		],
	);

	return annotationProvider ? (
		<RendererActionsContext>
			<WithRendererActions
				render={(actions) => {
					return (
						<Fragment>
							<PageSegmentLoadStart key={contentId} metric={FABRIC_COMMENTS_HIGHLIGHTS_METRIC} />
							<AnnotationsWrapper
								rendererRef={rendererRef}
								adfDocument={adfDocument}
								annotationProvider={annotationProvider}
								isNestedRender={isNestedRender}
								onLoadComplete={handleAnnotationsLoaded}
							>
								{!isNestedRender && <RendererAnnotationProviderComponents />}
								{selectionProps && !isNestedRender && (
									<SelectionComponentLoader
										{...selectionProps}
										isOffline={isOffline}
										contentType={contentType}
										lastModifiedDate={lastModifiedDate}
										adfDocument={adfDocument}
										getSelectionContext={() => actions.getSelectionContext()}
										isHighlightOptionPanelEnabled={isHighlightOptionPanelEnabled}
										preloadedCanAddComments={preloadedCanAddComments}
									/>
								)}
								{hoverProps && !isNestedRender && (
									<HoverComponentLoader
										{...hoverProps}
										isOffline={isOffline}
										preloadedCanAddComments={preloadedCanAddComments}
									/>
								)}
								{viewProps && !isNestedRender && (
									<ViewComponentLoader
										{...viewProps}
										isOffline={isOffline}
										preloadedCanAddComments={preloadedCanAddComments}
									/>
								)}
								{children}
							</AnnotationsWrapper>
							{!isNestedRender && (
								<>
									<InlineCommentQueryParamsListener
										isArchived={isArchived}
										rendererRef={rendererRef}
									/>
									<RendererActionsListener actions={actions} pageId={contentId} />
								</>
							)}

							<InlineCommentsPubSubHandler
								contentId={contentId!}
								contentType={contentType}
								updateDocument={updateDocument}
								rendererActions={actions}
							/>
							<ContentRefetchHandler
								contentId={contentId}
								shouldRefetch={refetchContent}
								refetchImmediately
								onRefetchComplete={handleOnRefetchComplete}
							/>
							<InlineCommentLinkHandlerLoader />
						</Fragment>
					);
				}}
			/>
		</RendererActionsContext>
	) : (
		<RendererActionsContext>
			<>{children}</>
		</RendererActionsContext>
	);
};

export const WithAnnotationsWrapper: React.ComponentType<
	Omit<WithAnnotationsWrapperProps, 'flags'>
> = withFlags(WithAnnotationsWrapperComponent);
